<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useFormatTime } from '@/Composables/useFormatTime'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faPrint, faTimes, faTruck } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'
library.add(faPrint, faTimes, faTruck)

const SLOTS_PER_PAGE = 4

const props = defineProps<{
    deliveryNotes: any[]
}>()

const isOpen = ref(false)
const printTime = ref('')

const openPreview = () => {
    const now = new Date()
    const weekday = now.toLocaleDateString('id-ID', { weekday: 'long' })
    const date = now.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })
    const time = now.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
    printTime.value = `${weekday}, ${date} ${time} WIB`
    isOpen.value = true
}

const pages = computed<any[][]>(() => {
    const result: any[][] = []
    let currentPage: any[] = []
    let slotsUsed = 0

    for (const note of props.deliveryNotes) {
        if (slotsUsed + 1 > SLOTS_PER_PAGE) {
            result.push(currentPage)
            currentPage = [note]
            slotsUsed = 1
        } else {
            currentPage.push(note)
            slotsUsed += 1
        }
    }

    if (currentPage.length > 0) result.push(currentPage)
    return result
})

const closePreview = () => { isOpen.value = false }

const onKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen.value) closePreview()
}

onMounted(() => window.addEventListener('keydown', onKeydown))
onUnmounted(() => window.removeEventListener('keydown', onKeydown))

const doPrint = () => {
    const now = new Date()
    const date = now.toLocaleDateString('sv-SE')
    const time = now.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', hour12: false })
    const filename = `Surat Jalan - ${date} ${time} - ${props.deliveryNotes.length}`

    const originalTitle = document.title
    document.title = filename
    window.print()
    document.title = originalTitle
}
</script>

<template>
    <div>
        <!-- Trigger button -->
        <button
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border"
            :class="deliveryNotes.length > 0
                ? 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'
                : 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'"
            :disabled="deliveryNotes.length === 0"
            @click="openPreview"
        >
            <FontAwesomeIcon icon="fal fa-print" fixed-width aria-hidden="true" />
            Cetak PDF
            <span class="ml-0.5 text-xs rounded-full px-1.5 leading-tight"
                :class="deliveryNotes.length > 0 ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-500'"
            >
                {{ deliveryNotes.length }}
            </span>
        </button>

        <!-- Print preview overlay -->
        <Teleport to="body">
            <div v-if="isOpen" class="dn-print-preview-overlay">

                <!-- Toolbar (hidden when printing) -->
                <div class="dn-print-toolbar no-print">
                    <div class="flex items-center gap-3">
                        <FontAwesomeIcon icon="fal fa-truck" class="text-emerald-600 text-lg" />
                        <div>
                            <div class="font-semibold text-gray-800 text-sm leading-tight">Preview Cetak Surat Jalan</div>
                            <div class="text-xs text-gray-500">
                                {{ deliveryNotes.length }} surat jalan dipilih &bull; {{ pages.length }} halaman
                            </div>
                        </div>
                    </div>
                    <div class="flex items-center gap-2">
                        <button
                            @click="doPrint"
                            class="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded hover:bg-emerald-700"
                        >
                            <FontAwesomeIcon icon="fal fa-print" />
                            Cetak / Simpan PDF
                        </button>
                        <button
                            @click="closePreview"
                            class="inline-flex items-center gap-1.5 px-3 py-2 text-gray-600 text-sm rounded hover:bg-gray-200"
                        >
                            <FontAwesomeIcon icon="fal fa-times" />
                            Tutup
                            <kbd class="ml-1 px-1 py-0.5 text-xs bg-gray-100 border border-gray-300 rounded text-gray-500">Esc</kbd>
                        </button>
                    </div>
                </div>

                <!-- Pages -->
                <div id="print-delivery-notes-area" class="dn-print-pages-wrapper">
                    <div
                        v-for="(page, pageIndex) in pages"
                        :key="pageIndex"
                        class="dn-print-page"
                    >
                        <div class="dn-page-grid">
                            <div
                                v-for="note in page"
                                :key="note.id"
                                class="dn-print-slot"
                            >
                                <!-- Slot Header -->
                                <div class="dn-slot-header">
                                    <div class="dn-slot-logo-row">
                                        <img src="/logo-kr-transparent.webp" class="dn-slot-logo" alt="Logo" />
                                        <div class="dn-slot-print-time">Diprint: {{ printTime }}</div>
                                    </div>
                                    <div class="dn-slot-reference">{{ note.reference }}</div>
                                    <!-- <div class="dn-slot-meta">Dibuat: {{ useFormatTime(note.created_at) }}</div> -->
                                    <div v-if="note.delivery_date" class="dn-slot-meta">
                                        Tgl. Pengiriman: <span class="font-medium text-gray-700">{{ useFormatTime(note.delivery_date) }}</span>
                                    </div>
                                    <div v-if="note.recipient_name" class="dn-slot-meta">
                                        Penerima: <span class="font-medium text-gray-700">{{ note.recipient_name }}</span>
                                    </div>
                                    <div v-if="note.recipient_address" class="dn-slot-meta italic opacity-70">
                                        {{ note.recipient_address }}
                                    </div>
                                    <div v-if="note.created_by_name" class="dn-slot-meta">
                                        Dibuat oleh: <span class="font-medium text-gray-700">{{ note.created_by_name }}</span>
                                    </div>
                                </div>

                                <!-- Orders Table -->
                                <div class="dn-slot-orders">
                                    <table class="dn-slot-orders-table">
                                        <thead>
                                            <tr>
                                                <th class="text-left">Produk</th>
                                                <th class="text-right w-8">Qty</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="order in note.orders" :key="order.id">
                                                <td class="truncate max-w-0" style="max-width: 1px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                                                    {{ order.product_name }}
                                                </td>
                                                <td class="text-right">{{ order.quantity }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <!-- Slot Footer -->
                                <div class="dn-slot-footer">
                                    <div class="flex justify-between items-center">
                                        <span class="dn-slot-count-label">Total Item</span>
                                        <span class="dn-slot-count-value">{{ note.orders_count }} order</span>
                                    </div>
                                    <div v-if="note.note" class="dn-slot-note">
                                        Catatan: {{ note.note }}
                                    </div>
                                    <!-- Signature area -->
                                    <div class="dn-slot-signature">
                                        <div class="dn-slot-signature-box">
                                            <div class="dn-slot-signature-label">Pengirim</div>
                                            <div class="dn-slot-signature-space"></div>
                                            <div class="dn-slot-signature-name">( ________________ )</div>
                                        </div>
                                        <div class="dn-slot-signature-box">
                                            <div class="dn-slot-signature-label">Penerima</div>
                                            <div class="dn-slot-signature-space"></div>
                                            <div class="dn-slot-signature-name">( ________________ )</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Page number indicator (screen only) -->
                        <div class="no-print dn-page-number-indicator">
                            Halaman {{ pageIndex + 1 }} dari {{ pages.length }}
                        </div>
                    </div>
                </div>

            </div>
        </Teleport>
    </div>
</template>

<style>
/* ===================== SCREEN STYLES ===================== */

.dn-print-preview-overlay {
    position: fixed;
    inset: 0;
    z-index: 200;
    background: #d1d5db;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.dn-print-toolbar {
    position: sticky;
    top: 0;
    z-index: 10;
    width: 100%;
    background: white;
    border-bottom: 1px solid #e5e7eb;
    padding: 12px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: 0 1px 3px rgba(0,0,0,.08);
}

.dn-print-pages-wrapper {
    padding: 24px 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    width: 100%;
}

/* A4 paper: 210mm × 297mm */
.dn-print-page {
    width: 210mm;
    min-height: 297mm;
    background: white;
    box-shadow: 0 4px 12px rgba(0,0,0,.15);
    padding: 10mm;
    box-sizing: border-box;
}

/* 2×2 grid for 4 slots */
.dn-page-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr;
    gap: 5mm;
    height: calc(297mm - 20mm);
}

/* Individual slot */
.dn-print-slot {
    border: 1px solid #d1d5db;
    border-radius: 2mm;
    padding: 4mm;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-sizing: border-box;
}

/* ---- Slot inner parts ---- */
.dn-slot-header {
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 2mm;
    margin-bottom: 2mm;
    flex-shrink: 0;
}

.dn-slot-reference {
    font-size: 10pt;
    font-weight: 700;
    color: #111827;
    line-height: 1.2;
    margin-bottom: 1mm;
}

.dn-slot-meta {
    font-size: 7pt;
    color: #6b7280;
    line-height: 1.4;
}

.dn-slot-orders {
    flex: 1;
    overflow: hidden;
}

.dn-slot-orders-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 7pt;
    table-layout: fixed;
}

.dn-slot-orders-table thead tr {
    border-bottom: 1px solid #d1d5db;
}

.dn-slot-orders-table th {
    font-weight: 600;
    color: #4b5563;
    padding: 0.5mm 0;
    font-size: 6.5pt;
}

.dn-slot-orders-table td {
    padding: 0.5mm 0;
    color: #1f2937;
    border-bottom: 1px solid #f3f4f6;
    line-height: 1.3;
    vertical-align: top;
}

.dn-slot-footer {
    border-top: 1px solid #e5e7eb;
    padding-top: 2mm;
    margin-top: 2mm;
    flex-shrink: 0;
}

.dn-slot-count-label {
    font-size: 7pt;
    color: #6b7280;
}

.dn-slot-count-value {
    font-size: 7.5pt;
    font-weight: 600;
    color: #111827;
}

.dn-slot-note {
    font-size: 6.5pt;
    color: #9ca3af;
    margin-top: 1mm;
    font-style: italic;
}

.dn-slot-signature {
    display: flex;
    justify-content: space-between;
    margin-top: 3mm;
    gap: 4mm;
}

.dn-slot-signature-box {
    flex: 1;
    text-align: center;
}

.dn-slot-signature-label {
    font-size: 6.5pt;
    color: #6b7280;
    margin-bottom: 6mm;
}

.dn-slot-signature-space {
    height: 8mm;
}

.dn-slot-signature-name {
    font-size: 6.5pt;
    color: #6b7280;
    border-top: 1px solid #d1d5db;
    padding-top: 1mm;
}

.dn-page-number-indicator {
    text-align: center;
    font-size: 11px;
    color: #9ca3af;
    margin-top: 8px;
}

/* Logo row inside each slot */
.dn-slot-logo-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5mm;
    padding-bottom: 1.5mm;
    border-bottom: 1px dashed #e5e7eb;
}

.dn-slot-logo {
    height: 6mm;
    width: auto;
    object-fit: contain;
}

.dn-slot-print-time {
    font-size: 5.5pt;
    color: #9ca3af;
    text-align: right;
    line-height: 1.3;
}

/* ===================== PRINT STYLES ===================== */
@media print {
    @page {
        size: A4;
        margin: 0;
    }

    body > * {
        visibility: hidden;
    }

    .dn-print-preview-overlay {
        position: absolute !important;
        top: 0 !important;
        left: 0 !important;
        overflow: visible !important;
        height: auto !important;
        background: none !important;
        display: block !important;
        width: 100% !important;
    }

    #print-delivery-notes-area,
    #print-delivery-notes-area * {
        visibility: visible;
    }

    #print-delivery-notes-area {
        position: static !important;
        padding: 0;
        gap: 0;
        display: block;
    }

    .no-print {
        display: none !important;
        visibility: hidden !important;
    }

    .dn-print-page {
        margin: 0;
        box-shadow: none;
        page-break-after: always;
        break-after: page;
        width: 210mm;
        min-height: 0;
        height: auto;
    }

    .dn-print-page:last-child {
        page-break-after: avoid;
        break-after: avoid;
    }

    .dn-page-grid {
        height: 253mm;
    }
}
</style>
